import { useCallback, useEffect, useState } from "react";
import Spinner from "../../components/ui/Spinner";
import { Help, ModalShell, btnGhost, btnPrimary, cardCls, errMsg, inputCls, labelCls } from "../../components/admin/promotionsUi";
import { useAdminToast } from "../../context/AdminToastContext";
import { formatISTDateTime } from "../../utils/dateUtils";
import { gamificationService, isApprovalRequired } from "../../features/gamification/service";
import type {
  ApprovalRow,
  BadgeDefinition,
  BadgeHolder,
  BadgeOverview,
  CreditReport,
  CreditReportRow,
  ReviewItem,
  UserSearchResult,
} from "../../features/gamification/types";
import { LoadError, PersonCell, Pager, StatusBadge, dash, tdCls, thCls, usePaged } from "./GamificationPanels";

const SOURCE_LABEL: Record<string, string> = {
  welcome_bonus: "Welcome bonus",
  coupon_bonus: "Top-up bonus (coupon)",
  coupon: "Flat coupon",
  campaign: "Festive campaign",
  quest: "Quests",
  referral: "Referral",
};
const label = (s: string) => SOURCE_LABEL[s] ?? s.replace(/_/g, " ");
const cr = (n: number) => (Math.round(n * 100) / 100).toLocaleString("en-IN");

// ── Report: free credits given / used / expired / taken back ─────────────────
function ReportTable({ rows, first }: { rows: (CreditReportRow & { key: string })[]; first: string }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="text-xs text-gray-500">
            <th className={thCls}>{first}</th>
            <th className={thCls}>Given</th>
            <th className={thCls}>Used</th>
            <th className={thCls}>Expired</th>
            <th className={thCls}>Taken back</th>
            <th className="pb-2 font-medium">Still on balance</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-t border-gray-800 text-gray-300">
              <td className={tdCls}>{r.key}</td>
              <td className={tdCls}>{cr(r.given)}</td>
              <td className={tdCls}>{cr(r.used)}</td>
              <td className={tdCls}>{cr(r.expired)}</td>
              <td className={tdCls}>{cr(r.taken_back)}</td>
              <td className="py-2">{cr(r.outstanding)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function ReportPanel() {
  const toast = useAdminToast();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState<CreditReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async (f: string, t: string) => {
    setLoading(true);
    try {
      setData(await gamificationService.getCreditReport(f, t));
      setError("");
    } catch (e) {
      setData(null);
      setError(errMsg(e, "Failed to load the report"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load("", ""); }, [load]);

  const exportCsv = async () => {
    setExporting(true);
    try {
      await gamificationService.exportCreditReport(from, to);
    } catch (e) {
      toast.showError(errMsg(e, "Failed to export the report"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <section className={cardCls}>
      <h2 className="text-white font-semibold mb-1">Free credits report</h2>
      <p className="text-gray-500 text-xs mb-4">
        Free credits given, used, expired and taken back, by source and by month (India calendar). Credits paid for by users are not included.
        Each row counts the credits given in that month: what became of them is shown on the same row.
        <span className="block text-gray-600">Example: June, "Quests": 1,000 given, 600 used, 100 expired, 0 taken back, 300 still on balances.</span>
      </p>
      <form className="flex flex-wrap items-end gap-3 mb-4" onSubmit={(e) => { e.preventDefault(); void load(from, to); }}>
        <div>
          <label className={labelCls} htmlFor="gr-from">From month</label>
          <input id="gr-from" type="month" className={inputCls} value={from} onChange={(e) => setFrom(e.target.value)} />
        </div>
        <div>
          <label className={labelCls} htmlFor="gr-to">To month</label>
          <input id="gr-to" type="month" className={inputCls} value={to} onChange={(e) => setTo(e.target.value)} />
        </div>
        <button type="submit" className={btnPrimary} disabled={loading}>Show</button>
        <button type="button" className={btnGhost} onClick={exportCsv} disabled={exporting}>{exporting ? "Preparing…" : "Download CSV"}</button>
        <Help>Leave both blank for the last 6 months. At most 24 months.</Help>
      </form>
      {loading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : !data ? (
        <LoadError message={error} onRetry={() => void load(from, to)} />
      ) : (
        <div className="space-y-6">
          <p className="text-gray-400 text-xs">
            {data.month_from} to {data.month_to}: given <strong className="text-gray-200">{cr(data.totals.given)}</strong>, used {cr(data.totals.used)}, expired {cr(data.totals.expired)}, taken back {cr(data.totals.taken_back)}, still on balances {cr(data.totals.outstanding)} credits.
          </p>
          <div>
            <h3 className="text-gray-200 text-sm font-semibold mb-2">By source</h3>
            <ReportTable first="Source" rows={data.by_source.map((r) => ({ ...r, key: label(r.source) }))} />
          </div>
          <div>
            <h3 className="text-gray-200 text-sm font-semibold mb-2">By month</h3>
            <ReportTable first="Month" rows={data.by_month.map((r) => ({ ...r, key: r.month }))} />
          </div>
          <details>
            <summary className="text-gray-300 text-sm cursor-pointer">Every month and source</summary>
            <div className="mt-2">
              <ReportTable first="Month / source" rows={data.matrix.map((r) => ({ ...r, key: `${r.month} · ${label(r.source)}` }))} />
            </div>
          </details>
          <details>
            <summary className="text-gray-300 text-sm cursor-pointer">Double-check these numbers (optional)</summary>
            <div className="mt-2 space-y-3 text-sm">
              <p className="text-gray-400 text-xs">
                The same free credits are written down in two separate places. If they agree, the report above is right.
                Left: what the quest system says it paid. Right: what actually landed in users' wallets, by kind.
                The "granted" quest credits on the left should equal the "quest reward" row on the right for the same month.
                Other kinds on the right (welcome bonus, coupon, campaign bonus, referral) are not quests, so they appear only there.
                <span className="block text-gray-600">Example: October, left "granted: 6 grants, 270 credits"; right "quest reward: 4 rows, 100 credits". The gap is 170 credits: referral rewards are written as grants on the left but may show under another kind on the right, and a reward that is still being paid has not reached the wallet yet. A big gap you cannot explain this way is worth a look.</span>
              </p>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <p className="text-gray-300 text-xs font-semibold mb-1">What the quests paid (by month the reward was created)</p>
                  {data.grants.length === 0 ? <p className="text-gray-500">None.</p> : (
                    <table className="w-full text-sm"><thead>
                      <tr className="text-xs text-gray-500"><th className="pb-1 text-left font-medium">Month</th><th className="pb-1 text-center font-medium">State</th><th className="pb-1 text-center font-medium">Rewards</th><th className="pb-1 text-center font-medium">Credits</th></tr>
                    </thead><tbody>
                      {data.grants.map((g) => (
                        <tr key={`${g.month}-${g.status}`} className="border-t border-gray-800 text-gray-300 text-center">
                          <td className={`${tdCls} text-left`}>{g.month}</td><td className={tdCls}><StatusBadge status={g.status} /></td>
                          <td className={tdCls}>{g.grants}</td><td className="py-2">{cr(g.credits)}</td>
                        </tr>
                      ))}
                    </tbody></table>
                  )}
                </div>
                <div>
                  <p className="text-gray-300 text-xs font-semibold mb-1">What landed in wallets (by month, by kind)</p>
                  {data.ledger.length === 0 ? <p className="text-gray-500">None.</p> : (
                    <table className="w-full text-sm"><thead>
                      <tr className="text-xs text-gray-500"><th className="pb-1 text-left font-medium">Month</th><th className="pb-1 text-left font-medium">Kind</th><th className="pb-1 text-center font-medium">Wallet entries</th><th className="pb-1 text-center font-medium">Credits</th></tr>
                    </thead><tbody>
                      {data.ledger.map((g) => (
                        <tr key={`${g.month}-${g.type}`} className="border-t border-gray-800 text-gray-300">
                          <td className={tdCls}>{g.month}</td><td className={`${tdCls} capitalize`}>{g.type.replace(/_/g, " ")}</td>
                          <td className={`${tdCls} text-center`}>{g.rows}</td><td className="py-2 text-center">{cr(g.credits)}</td>
                        </tr>
                      ))}
                    </tbody></table>
                  )}
                </div>
              </div>
            </div>
          </details>
        </div>
      )}
    </section>
  );
}

// ── Approvals ────────────────────────────────────────────────────────────────
const APPROVAL_TONE: Record<string, string> = {
  pending: "bg-amber-900/40 text-amber-300",
  approved: "bg-emerald-900/40 text-emerald-300",
  rejected: "bg-red-900/40 text-red-300",
  failed: "bg-red-900/40 text-red-300",
  withdrawn: "bg-gray-800 text-gray-400",
  expired: "bg-gray-800 text-gray-400",
};
const KIND_LABEL: Record<string, string> = { config: "Settings", quest: "Quest budget", takeback: "Take-back", referral: "Referral settings", campaign_apply: "Campaign apply" };

export function ApprovalsPanel() {
  const toast = useAdminToast();
  const [status, setStatus] = useState("pending");
  const fetcher = useCallback((page: number) => gamificationService.listApprovals(status, page), [status]);
  const { data, loading, error, load } = usePaged<ApprovalRow>(fetcher, "Failed to load approvals");
  useEffect(() => { void load(1); }, [load]);
  const [target, setTarget] = useState<{ row: ApprovalRow; action: "approve" | "reject" } | null>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const decide = async () => {
    if (!target) return;
    setBusy(true);
    try {
      if (target.action === "approve") {
        const res = await gamificationService.approveRequest(target.row.id, note);
        if (res.status === "approved") toast.showSuccess("Approved and applied");
        else toast.showError(`Approved, but the change could not be applied${res.error ? `: ${res.error}` : ""}`);
      } else {
        await gamificationService.rejectRequest(target.row.id, note);
        toast.showSuccess("Rejected");
      }
      setTarget(null);
      setNote("");
      void load(1);
    } catch (e) {
      toast.showError(errMsg(e, "Failed to record the decision"));
    } finally {
      setBusy(false);
    }
  };

  const withdraw = async (row: ApprovalRow) => {
    try {
      await gamificationService.withdrawRequest(row.id);
      toast.showSuccess("Request withdrawn");
      void load(1);
    } catch (e) {
      toast.showError(errMsg(e, "Failed to withdraw (only the admin who asked can)"));
    }
  };

  return (
    <section className={cardCls}>
      <h2 className="text-white font-semibold mb-1">Approvals</h2>
      <p className="text-gray-500 text-xs mb-4">
        With "Require a second admin" ON in Settings, risky changes wait here: turning rewards on, paying existing users, raising a quest budget past the limit, and large take-backs. A different admin must approve; you cannot approve your own request. Requests expire on their own.
      </p>
      <div className="flex flex-wrap items-end gap-3 mb-2">
        <div>
          <label className={labelCls} htmlFor="ga-status">Show</label>
          <select id="ga-status" className={inputCls} value={status} onChange={(e) => setStatus(e.target.value)}>
            {["pending", "approved", "rejected", "failed", "withdrawn", "expired", ""].map((s) => <option key={s} value={s}>{s || "All"}</option>)}
          </select>
        </div>
      </div>
      {loading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : !data ? (
        <LoadError message={error} onRetry={() => void load(1)} />
      ) : data.items.length === 0 ? (
        <p className="text-gray-500 text-sm py-4">No {status || ""} requests.</p>
      ) : (
        <>
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-xs text-gray-500">
                  <th className={thCls}>What</th>
                  <th className={thCls}>Asked by</th>
                  <th className={thCls}>When</th>
                  <th className={thCls}>Status</th>
                  <th className={thCls}>Decided by</th>
                  <th className="pb-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {data.items.map((r) => (
                  <tr key={r.id} className="border-t border-gray-800 text-gray-300 align-top">
                    <td className={`${tdCls} max-w-[22rem]`}>
                      <p className="text-xs text-gray-500">{KIND_LABEL[r.kind] ?? r.kind}</p>
                      <p>{r.summary}</p>
                      {r.user_id && <div className="text-xs text-gray-400 mt-1">For <PersonCell id={r.user_id} name={r.user_name} email={r.user_email} phone={r.user_phone} /></div>}
                      {r.reason && <p className="text-xs text-gray-500 mt-1">Reason: {r.reason}</p>}
                      {r.error && <p className="text-xs text-red-400 mt-1">Could not apply: {r.error}</p>}
                    </td>
                    <td className={tdCls}><PersonCell id={r.requested_by} name={r.requested_by_name} email={r.requested_by_email} phone={r.requested_by_phone} /></td>
                    <td className={`${tdCls} whitespace-nowrap text-xs`}>
                      {formatISTDateTime(r.requested_at)}
                      {r.status === "pending" && <span className="block text-gray-500">expires {formatISTDateTime(r.expires_at)}</span>}
                    </td>
                    <td className={tdCls}><span className={`px-2 py-0.5 rounded-full text-xs ${APPROVAL_TONE[r.status] ?? "bg-gray-800 text-gray-300"}`}>{r.status}</span></td>
                    <td className={tdCls}>
                      {r.decided_by ? <PersonCell id={r.decided_by} name={r.decided_by_name} email={r.decided_by_email} phone={r.decided_by_phone} /> : "—"}
                      {r.decision_note && <p className="text-xs text-gray-500 mt-1">{r.decision_note}</p>}
                    </td>
                    <td className="py-2 whitespace-nowrap">
                      {r.status === "pending" && (
                        <div className="flex gap-1">
                          <button className={btnPrimary} onClick={() => { setTarget({ row: r, action: "approve" }); setNote(""); }}>Approve</button>
                          <button className={btnGhost} onClick={() => { setTarget({ row: r, action: "reject" }); setNote(""); }}>Reject</button>
                          <button className={btnGhost} onClick={() => void withdraw(r)} title="Only the admin who asked can withdraw">Withdraw</button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager data={data} busy={loading} onPage={(p) => void load(p)} />
        </>
      )}
      {target && (
        <ModalShell title={target.action === "approve" ? "Approve this change?" : "Reject this request?"} onClose={() => !busy && setTarget(null)}>
          <p className="text-sm text-gray-300 mb-3">{target.row.summary}</p>
          <label className={labelCls} htmlFor="ga-note">{target.action === "approve" ? "Note (optional)" : "Reason (required)"}</label>
          <textarea id="ga-note" className={inputCls} rows={3} maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} />
          <p className="text-gray-500 text-xs mt-2">{target.action === "approve" ? "The change is applied right away and recorded in the audit log under your name." : "Nothing changes. The reason is saved in the audit log."}</p>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setTarget(null)} disabled={busy} className={btnGhost}>Cancel</button>
            <button onClick={decide} disabled={busy || (target.action === "reject" && !note.trim())} className={btnPrimary}>
              {busy ? "Saving…" : target.action === "approve" ? "Approve" : "Reject"}
            </button>
          </div>
        </ModalShell>
      )}
    </section>
  );
}

// ── Badges ───────────────────────────────────────────────────────────────────
export function BadgesPanel({ onOpenSettings }: { onOpenSettings?: () => void }) {
  const toast = useAdminToast();
  const [overview, setOverview] = useState<BadgeOverview | null>(null);
  const [loadError, setLoadError] = useState("");
  const [badge, setBadge] = useState<BadgeDefinition | null>(null);
  const [includeRevoked, setIncludeRevoked] = useState(false);

  const loadOverview = useCallback(async () => {
    try {
      const o = await gamificationService.getBadges();
      setOverview(o);
      setBadge((cur) => o.items.find((b) => b.badge_id === cur?.badge_id) ?? o.items[0] ?? null);
      setLoadError("");
    } catch (e) {
      setOverview(null);
      setLoadError(errMsg(e, "Failed to load badges"));
    }
  }, []);
  useEffect(() => { void loadOverview(); }, [loadOverview]);

  const badgeId = badge?.badge_id ?? "";
  const fetcher = useCallback((page: number) => gamificationService.listBadgeHolders(badgeId, includeRevoked, page), [badgeId, includeRevoked]);
  const { data, loading, error, load } = usePaged<BadgeHolder>(fetcher, "Failed to load the badge holders");
  useEffect(() => { if (badgeId) void load(1); }, [load, badgeId]);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<UserSearchResult[] | null>(null);
  const [action, setAction] = useState<{ kind: "award" | "revoke"; userId: string; who: string } | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const search = async () => {
    if (query.trim().length < 3) return toast.showError("Type at least 3 characters of a name, email or phone number.");
    try {
      setResults(await gamificationService.searchUsers(query.trim(), 10));
    } catch (e) {
      setResults(null);
      toast.showError(errMsg(e, "Couldn't search for users right now."));
    }
  };

  const run = async () => {
    if (!action || !badge || !reason.trim()) return;
    setBusy(true);
    try {
      if (action.kind === "award") await gamificationService.awardBadge(badge.badge_id, action.userId, reason);
      else await gamificationService.revokeBadge(badge.badge_id, action.userId, reason);
      toast.showSuccess(action.kind === "award" ? "Badge awarded" : "Badge revoked");
      setAction(null);
      setReason("");
      setResults(null);
      void load(1);
      void loadOverview();
    } catch (e) {
      toast.showError(errMsg(e, "Failed to change the badge"));
    } finally {
      setBusy(false);
    }
  };

  if (!overview) {
    return <section className={cardCls}>{loadError ? <LoadError message={loadError} onRetry={() => void loadOverview()} /> : <div className="flex justify-center py-8"><Spinner /></div>}</section>;
  }
  const fr = overview.fast_responder;
  return (
    <div className="space-y-6">
      <section className={cardCls}>
        <h2 className="text-white font-semibold mb-1">Badges</h2>
        <p className="text-gray-500 text-xs mb-4">
          Badges pay nothing. Verified and Fast Responder follow the user's state and are re-checked every week; a badge you award or revoke by hand here is kept, and the weekly check will not undo it.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {overview.items.map((b) => (
            <button key={b.badge_id} onClick={() => setBadge(b)} aria-pressed={badge?.badge_id === b.badge_id}
              className={`text-left rounded-xl border p-4 ${badge?.badge_id === b.badge_id ? "border-indigo-500 bg-indigo-950/30" : "border-gray-800 bg-gray-800/40 hover:border-gray-700"}`}>
              <p className="text-white font-semibold">{b.name}</p>
              <p className="text-2xl font-bold text-gray-100 mt-1">{b.holders}<span className="text-xs text-gray-500 font-normal"> holders{b.manual_holders > 0 ? ` (${b.manual_holders} by hand)` : ""}</span></p>
              <p className="text-gray-500 text-xs mt-2">{b.description}</p>
            </button>
          ))}
        </div>
        <p className="text-gray-400 text-xs mt-4">
          Fast Responder limits: median first reply within {fr.max_minutes} business minutes, at least {fr.min_enquiries} answered enquiries, looking back {fr.window_days} days.
          {onOpenSettings && <> <button className="underline text-indigo-300" onClick={onOpenSettings}>Change in Settings</button></>}
        </p>
      </section>

      {badge && (
        <section className={cardCls}>
          <h2 className="text-white font-semibold mb-3">Award "{badge.name}" by hand</h2>
          <form className="flex flex-wrap items-end gap-2 mb-3" onSubmit={(e) => { e.preventDefault(); void search(); }}>
            <div>
              <label className={labelCls} htmlFor="gb-search">Find the user by name, email or phone</label>
              <input id="gb-search" className={`${inputCls} max-w-sm`} value={query} onChange={(e) => setQuery(e.target.value)} />
            </div>
            <button type="submit" className={btnPrimary} disabled={query.trim().length < 3}>Search</button>
          </form>
          {results && results.length === 0 && <p className="text-gray-500 text-sm mb-2">No users match.</p>}
          {results && results.length > 0 && (
            <ul className="mb-3 divide-y divide-gray-800 border border-gray-800 rounded-lg">
              {results.map((u) => (
                <li key={u.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm text-gray-300">
                  <PersonCell id={u.id} name={u.name} email={u.email} phone={u.phone} />
                  <button className={btnGhost} onClick={() => { setAction({ kind: "award", userId: u.id, who: u.name || u.email || u.id }); setReason(""); }}>Award</button>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      {badge && (
        <section className={cardCls}>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h2 className="text-white font-semibold">Who holds "{badge.name}"</h2>
            <label className="flex items-center gap-2 text-xs text-gray-400">
              <input type="checkbox" checked={includeRevoked} onChange={(e) => setIncludeRevoked(e.target.checked)} />
              Include revoked
            </label>
          </div>
          {loading ? (
            <div className="flex justify-center py-8"><Spinner /></div>
          ) : !data ? (
            <LoadError message={error} onRetry={() => void load(1)} />
          ) : data.items.length === 0 ? (
            <p className="text-gray-500 text-sm py-2">Nobody holds this badge.</p>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="text-xs text-gray-500">
                      <th className={thCls}>User</th>
                      <th className={thCls}>Since</th>
                      <th className={thCls}>How</th>
                      <th className="pb-2 font-medium" />
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((h) => (
                      <tr key={h.user_id} className="border-t border-gray-800 text-gray-300 align-top">
                        <td className={tdCls}><PersonCell id={h.user_id} name={h.user_name} email={h.user_email} phone={h.user_phone} /></td>
                        <td className={`${tdCls} whitespace-nowrap`}>
                          {formatISTDateTime(h.awarded_at)}
                          {h.revoked_at && <span className="block text-xs text-red-400">revoked {formatISTDateTime(h.revoked_at)}</span>}
                        </td>
                        <td className={`${tdCls} text-xs max-w-[18rem]`}>
                          {h.manual ? <>By hand{h.awarded_by ? <> by <PersonCell id={h.awarded_by} name={h.awarded_by_name} email={h.awarded_by_email} /></> : null}</> : h.manual_revoked ? "Revoked by hand" : "Automatic"}
                          {h.reason && <p className="text-gray-500">{dash(h.reason)}</p>}
                        </td>
                        <td className="py-2">
                          {!h.revoked_at && (
                            <button className={btnGhost} onClick={() => { setAction({ kind: "revoke", userId: h.user_id, who: h.user_name || h.user_email || h.user_id }); setReason(""); }}>Revoke</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pager data={data} busy={loading} onPage={(p) => void load(p)} />
            </>
          )}
        </section>
      )}

      {action && badge && (
        <ModalShell title={`${action.kind === "award" ? "Award" : "Revoke"} "${badge.name}" ${action.kind === "award" ? "to" : "from"} ${action.who}?`} onClose={() => !busy && setAction(null)}>
          <label className={labelCls} htmlFor="gb-reason">Reason (required, saved in the audit log)</label>
          <textarea id="gb-reason" className={inputCls} rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} />
          <p className="text-gray-500 text-xs mt-2">The weekly check will not undo this.</p>
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setAction(null)} disabled={busy} className={btnGhost}>Cancel</button>
            <button onClick={run} disabled={busy || !reason.trim()} className={btnPrimary}>{busy ? "Saving…" : action.kind === "award" ? "Award" : "Revoke"}</button>
          </div>
        </ModalShell>
      )}
    </div>
  );
}

// ── Review queue ─────────────────────────────────────────────────────────────
export function ReviewQueuePanel() {
  const toast = useAdminToast();
  const fetcher = useCallback((page: number) => gamificationService.listReviewQueue(page), []);
  const { data, loading, error, load } = usePaged<ReviewItem>(fetcher, "Failed to load the review queue");
  useEffect(() => { void load(1); }, [load]);
  const [target, setTarget] = useState<{ item: ReviewItem; action: "retry" | "dismiss" } | null>(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);

  const run = async () => {
    if (!target || !text.trim()) return;
    setBusy(true);
    try {
      if (target.action === "dismiss") {
        await gamificationService.dismissReviewItem(target.item.user_id, target.item.grant_key, text);
        toast.showSuccess("Dismissed");
      } else {
        const res = await gamificationService.retryReviewItem(target.item.user_id, target.item.grant_key, text);
        if (isApprovalRequired(res)) toast.showSuccess("Sent for approval (Approvals tab).");
        else if (res.status === "taken_back") toast.showSuccess("The reward was taken back.");
        else if (res.status === "below_floor") toast.showError("Still below the floor, so nothing was taken. Raise the floor in Settings or dismiss it.");
        else toast.showError(`Result: ${res.status.replace(/_/g, " ")}`);
      }
      setTarget(null);
      setText("");
      void load(1);
    } catch (e) {
      toast.showError(errMsg(e, "Failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={cardCls}>
      <h2 className="text-white font-semibold mb-1">Review queue</h2>
      <p className="text-gray-500 text-xs mb-4">
        Take-backs that would push a balance below the floor were not done and are listed here. Retry runs the normal take-back again (for example after raising the floor in Settings); Dismiss keeps the credit and removes it from this list. Both are saved in the audit log.
      </p>
      {loading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : !data ? (
        <LoadError message={error} onRetry={() => void load(1)} />
      ) : data.items.length === 0 ? (
        <p className="text-gray-500 text-sm py-4">Nothing to review.</p>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="text-xs text-gray-500">
                  <th className={thCls}>User</th>
                  <th className={thCls}>Credit</th>
                  <th className={thCls}>Balance now</th>
                  <th className={thCls}>Flagged</th>
                  <th className="pb-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {data.items.map((it) => (
                  <tr key={`${it.user_id}-${it.grant_key}`} className="border-t border-gray-800 text-gray-300 align-top">
                    <td className={tdCls}><PersonCell id={it.user_id} name={it.user_name} email={it.user_email} phone={it.user_phone} /></td>
                    <td className={tdCls}>{cr(it.credits)} credits<span className="block text-xs text-gray-500" title={it.grant_key}>{label(it.source ?? it.kind ?? "")}</span></td>
                    <td className={tdCls}>{it.balance_credits === null ? "—" : `${cr(it.balance_credits)} credits`}</td>
                    <td className={`${tdCls} whitespace-nowrap`}>{formatISTDateTime(it.flagged_at)}</td>
                    <td className="py-2 whitespace-nowrap">
                      <button className={btnGhost} onClick={() => { setTarget({ item: it, action: "retry" }); setText(""); }}>Retry take-back</button>
                      <button className={btnGhost} onClick={() => { setTarget({ item: it, action: "dismiss" }); setText(""); }}>Dismiss</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager data={data} busy={loading} onPage={(p) => void load(p)} />
        </>
      )}
      {target && (
        <ModalShell title={target.action === "retry" ? "Retry this take-back?" : "Dismiss this item?"} onClose={() => !busy && setTarget(null)}>
          <p className="text-sm text-gray-300 mb-3">
            {cr(target.item.credits)} credits from {target.item.user_name || target.item.user_email || target.item.user_id}.
          </p>
          <label className={labelCls} htmlFor="gq-text">{target.action === "retry" ? "Reason (required, saved in the audit log)" : "Note (required, saved in the audit log)"}</label>
          <textarea id="gq-text" className={inputCls} rows={3} maxLength={500} value={text} onChange={(e) => setText(e.target.value)} />
          <div className="flex justify-end gap-3 mt-4">
            <button onClick={() => setTarget(null)} disabled={busy} className={btnGhost}>Cancel</button>
            <button onClick={run} disabled={busy || !text.trim()} className={btnPrimary}>{busy ? "Saving…" : target.action === "retry" ? "Retry" : "Dismiss"}</button>
          </div>
        </ModalShell>
      )}
    </section>
  );
}
